import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  Button,
  IconArrow,
  IconAudioRecord,
  Tag,
} from 'gatorcomponents';
import * as Styles from './AiChatForm.styles';
import { AudioRecording } from '@/components/Layout/Menu/HeaderV2/AiChat/components/AudioRecording';
import { useAiChat } from '../..';
import { useFeatureIsOn, useFeatureValue } from '@growthbook/growthbook-react';
import { useAudioRecorder } from '@/hooks/useAudioRecorder';
import { ATIVACAO_AICHAT_AUDIO_RECORDING, ATIVACAO_N8N_INHOUSE_URLS, CHARLIE_ALLOW_MULTIAGENT_FOR_MARKETING } from '@/config/GrowthBook/constants';
import { useDispatch, useSelector } from 'react-redux';
import useLocale from '@/hooks/useLocale';
import AiIconImage from '@/media/MaiaIA/iconImage.png';
import listeningImage from '@/media/MaiaIA/listening.png';
import { MiniCard } from '@/pages/Home/components/AiConversationBanner/components';
import { merlinActions } from '@/redux/modules';
import { WSS_ROUTES } from '@/components/Layout/Menu/HeaderV2/AiChat/AiChat.enum';
import { OPTIONS } from '@/components/Layout/Menu/HeaderV2/components/NewAiHeaderButton/NewAiHeaderButton.types';
import { useResize } from '@/hooks/useResize';
import { useChatbot } from '@/contexts/Chatbot';
import { AiAlertsCta } from '@/components/Layout/Menu/HeaderV2/AiChat/components/AiAlertsCta';
import { gatorAnalytics } from '@/analytics/gator';

const AiChatForm = ({
  onChange,
  onKeyDown,
  onSubmit,
  testId = 'aichat-form',
  withoutMargin = false,
}) => {
  const windowRect = useResize();
  const isMobile = windowRect.width < 600;

  const dispatch = useDispatch();
  const chatbot = useChatbot();
  const formRef = useRef(null);
  const textareaRef = useRef(null);
  const { aichat: aiChatLocale } = useLocale();
  const { audioRecording: audioRecordingLocale, headerLocale, alertsCta: alertsCtaLocale } = aiChatLocale;
  const { triggerExternalMessage, triggerExternalAudioBlob } = useAiChat();
  const { recording, handleStartRecording, handleStopRecording } = useAudioRecorder({
    onStop: audioBlob => triggerExternalAudioBlob(audioBlob),
  });

  const [messageContent, setMessageContent] = useState('');
  const [renderedSuggestions, setRenderedSuggestions] = useState(headerLocale.options.filter(item => !item.showMoreOptions));
  const [interactedWithSuggestions, setInteractedWithSuggestions] = useState(false);
  const [moreThanOneLine, setMoreThanOneLine] = useState(false);
  const {
    currentChat, loadingTranscription, alertsTypes, alerts,
  } = useSelector(state => state.merlin);

  const isAudioRecordingFeatureActive = useFeatureIsOn(ATIVACAO_AICHAT_AUDIO_RECORDING);
  const allowMultiAgent = useFeatureIsOn(CHARLIE_ALLOW_MULTIAGENT_FOR_MARKETING);
  const wssToggleRoutes = useFeatureValue(ATIVACAO_N8N_INHOUSE_URLS);

  const isRecording = recording || loadingTranscription;
  const chatIsOpen = Boolean(currentChat && currentChat.threadid);
  const isChatbotLoading = chatbot.isActive && !chatbot.isReady;
  const disabled = chatIsOpen || isChatbotLoading;

  const handleSubmit = (event) => {
    event.preventDefault();

    if (chatIsOpen || !messageContent) {
      return;
    }

    if (!chatbot.isActive) {
      const route = wssToggleRoutes.general || WSS_ROUTES.GENERAL;
      dispatch(merlinActions.inhouse.setRoute(route));
    } else {
      chatbot.setRoute('general');
    }
    triggerExternalMessage({ message: messageContent.trim() });
    setMessageContent('');
    onSubmit && onSubmit();
  };

  const handleChange = (event) => {
    setMessageContent(event.target.value);
    onChange && onChange(event);
  };

  const onKeyDownHandler = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      handleSubmit(event);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', onKeyDownHandler);
    return () => {
      document.removeEventListener('keydown', onKeyDownHandler);
    };
  });

  const handleKeyDown = (event) => {
    onKeyDown && onKeyDown(event);
  };

  const getTextWidth = (text, font) => {
    const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement('canvas'));
    const context = canvas.getContext('2d');
    context.font = font;
    const metrics = context.measureText(text);
    return metrics.width;
  };

  const handleInput = (text) => {
    const wrapper = formRef.current;
    const textArea = textareaRef.current;
    if (!textArea) return;

    const style = getComputedStyle(formRef.current);
    const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;

    const longestLine = text.split('\n').reduce((a, b) => (a.length > b.length ? a : b), '');

    const textWidth = Math.round(getTextWidth(longestLine, font));
    const defaultHeight = 24;
    const maxHeight = 200;
    const imageWidth = 32;
    const actionsWidth = 95;
    const tolerance = 0;

    const wrapperWithTolerance = wrapper.offsetWidth - imageWidth - actionsWidth - tolerance;

    textArea.style.height = `${defaultHeight}px`;
    if (textWidth < wrapperWithTolerance) {
      textArea.style.width = `calc(100% - ${imageWidth}px - ${actionsWidth}px)`;
      setMoreThanOneLine(false);
    }

    if (textWidth >= wrapperWithTolerance) {
      textArea.style.width = `calc(100% - ${imageWidth}px)`;
      const newHeight = Math.min(textareaRef.current.scrollHeight, maxHeight);
      textArea.style.height = `${newHeight >= maxHeight ? maxHeight : textArea.scrollHeight}px`;
      if (newHeight >= maxHeight) {
        textArea.style.overflow = 'auto';
      }
      setMoreThanOneLine(true);
    }
  };

  const handleExpandSuggestions = () => {
    setRenderedSuggestions(headerLocale.options.filter(suggestion => suggestion.value !== OPTIONS.MORE));
  };

  const dispatchGaEvent = ({ value }) => {
    if (value === OPTIONS.MORE) return;
    gatorAnalytics.gatorShortcut({ label: value });
  };

  const handleSelectSuggestion = ({ value, message, websocketType = 'n8n' }) => {
    if (value === OPTIONS.MORE) {
      handleExpandSuggestions();
      return;
    }

    if (!chatbot.isActive) {
      const routes = {
        [OPTIONS.CREATE_EMAIL]: wssToggleRoutes.email || WSS_ROUTES.EMAIL,
        [OPTIONS.CONFIGURE_DOMAIN]: wssToggleRoutes.domain || WSS_ROUTES.DOMAIN,
        [OPTIONS.CREATE_SITE]: wssToggleRoutes.site || WSS_ROUTES.SITE,
        general: wssToggleRoutes.general || WSS_ROUTES.GENERAL,
      };

      dispatch(merlinActions.inhouse.setRoute(routes[value] || routes.general));
    } else {
      const routes = {
        [OPTIONS.CREATE_EMAIL]: 'email',
        [OPTIONS.CONFIGURE_DOMAIN]: 'domain',
        [OPTIONS.CREATE_SITE]: 'site',
        general: 'general',
      };
      chatbot.setRoute(routes[value] || routes.general);
    }
    triggerExternalMessage({ message, websocketType });
  };

  const handleScrollSuggestions = () => {
    setInteractedWithSuggestions(true);
  };

  const renderSuggestions = ({ noWrap = false } = {}) => renderedSuggestions.map(suggestion => (
    <MiniCard
      key={suggestion.id || suggestion.label}
      Icon={suggestion.Icon}
      label={suggestion.label}
      onClick={() => {
        dispatchGaEvent({ value: suggestion.value });
        handleSelectSuggestion({
          value: suggestion.value,
          message: suggestion.message,
          websocketType: allowMultiAgent ? suggestion.websocketType : 'n8n',
        });
      }}
      disabled={disabled}
      noWrap={noWrap}
    />
  ));

  const placeholderMessage = isMobile ? headerLocale.talkToMaiaMobile : headerLocale.talkToMaia;

  const getAiAlertsCtaProps = useCallback(() => {
    if (!alertsTypes) {
      return;
    }

    const alertsArrays = alertsTypes && Object.values(alertsTypes);
    const hasLoaded = alertsArrays.some(alert => alert.loaded);

    return ({
      label: alertsCtaLocale && alertsCtaLocale.label,
      cta: alertsCtaLocale.cta,
      alerts,
      loading: !hasLoaded,
      alertsTypes,
    });
  }, [alertsCtaLocale, alertsTypes, alerts]);


  return (
    <Styles.Gradient id="gradient" $withoutMargin={withoutMargin}>
      <Styles.TagWrapper>
        <Tag label={aiChatLocale.tag} variant="info" size="sm" />
      </Styles.TagWrapper>

      <Styles.ConversationBox id="conversation-box">
        <Styles.AiChatFormAdapter id="aichat-form-adapter">
          <Styles.FormWrapper
            $disabled={disabled}
            id="form-wrapper"
            $moreThanOneLine={moreThanOneLine}
          >
            <Styles.Form
              ref={formRef}
              onSubmit={handleSubmit}
              $disabled={disabled}
              id="aichat-form"
              data-testid={testId}
              $isRecording={isRecording}
              $moreThanOneLine={moreThanOneLine}
            >
              <Styles.Image
                src={isRecording ? listeningImage : AiIconImage}
              />

              {!isRecording && (
                <>
                  <Styles.TextArea
                    placeholder={chatIsOpen
                      ? aiChatLocale.headerLocale.alreadyOpened
                      : placeholderMessage
                    }
                    value={messageContent}
                    onChange={(e) => {
                      handleChange(e);
                      handleInput(e.target.value);
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                    id="aichat-textarea"
                    data-testid={`${testId}-textarea`}
                    ref={textareaRef}
                  />

                  <Styles.ActionsWrapper data-testid={`${testId}-actions-wrapper`}>
                    {isAudioRecordingFeatureActive && (
                    <Styles.ButtonRecordingAdapter onClick={handleStartRecording} disabled={disabled} data-testid={`${testId}-button-recording`}>
                      <IconAudioRecord size="md" color={disabled ? 'highDark' : 'lowDark'} />
                    </Styles.ButtonRecordingAdapter>
                    )}
                    <Styles.ButtonSubmitAdapter>
                      <Button
                        type="submit"
                        variant="primary"
                        width="fitContent"
                        label={<IconArrow direction="top" />}
                        disabled={!messageContent || disabled}
                        testId="submit"
                      />
                    </Styles.ButtonSubmitAdapter>
                  </Styles.ActionsWrapper>
                </>
              )}

              {isRecording && (
                <AudioRecording
                  onCancel={() => handleStopRecording({ cancelRecording: true })}
                  onSend={() => handleStopRecording({ cancelRecording: false })}
                  isRecording={recording}
                  transcribing={loadingTranscription}
                  locales={audioRecordingLocale}
                  whiteBackground
                />
              )}
            </Styles.Form>
          </Styles.FormWrapper>
        </Styles.AiChatFormAdapter>
      </Styles.ConversationBox>

      <Styles.WhiteBox onScroll={setInteractedWithSuggestions}>
        <Styles.SuggestionsWrapper
          data-testid="suggestions-wrapper"
          data-interacted={interactedWithSuggestions}
          $interacted={interactedWithSuggestions}
          onScroll={handleScrollSuggestions}
        >

          <AiAlertsCta {...getAiAlertsCtaProps()} />
          {renderSuggestions({ noWrap: true })}
        </Styles.SuggestionsWrapper>
      </Styles.WhiteBox>
    </Styles.Gradient>
  );
};

export default AiChatForm;
